import type { Answer } from 'types/answers'
import { payloadMediaKind } from './Answer/payloadMediaKind'
import { getAnswerDisplayParts } from './answerDisplayText'
import {
    answerImageInputClassificationItem,
    aiInputDescriptorForMediaItem,
    aiInputDescriptorsForAnswer,
    aiRunOutputIsConsumedInput,
    answerRecordingInputClassificationItem,
    fileInputClassificationItem,
} from 'lib/aiInputMediaClassification'
import { firstValidMediaTimestamp, sortMediaByCreation } from 'lib/mediaDisplayOrder'

// What a quote line shows for a quoted post: its action tags, small media
// placeholders, and the leftover text. See docs/quote-line-media-and-action-tags.md
// for the scenario spec; tests/unit/quotePreview.test.ts encodes it.
//
// Pure by construction — nothing here reaches state, the DOM, or a stylesheet —
// so the whole rule set is unit-testable and QuoteLine renders without logic.

export type QuoteMediaKind = 'image' | 'video' | 'audio' | 'file' | 'link' | 'ordinal' | 'poll'

export interface QuoteMediaChip {
    kind: QuoteMediaKind
    // The small variant when the payload carries one; null renders the kind
    // glyph instead (and a failed <img> falls back to the same glyph).
    thumbUrl: string | null
    // Tooltip text, and the line's words when this leading chip has no
    // thumbnail and nothing else names the post.
    label: string
}

export interface QuotePreview {
    // Token labels in document order — `displayName` when the action has one,
    // else `!tag`. Never a prompt.
    actionTags: string[]
    media: QuoteMediaChip[]
    // Media past the cap, rendered as "+N".
    overflowCount: number
    text: string
}

export const MEDIA_CHIP_LIMIT = 3
export const ACTION_TAG_LIMIT = 2

// A 16px chip prefers the 120px `small` variant and can fall back to the
// generated medium preview, but it must never download a large/original asset.
// Answer::IMAGE_SIZES = { small: 120, medium: 600, large: 1050 }.
const IMAGE_VARIANT_KEYS = ['small', 'medium']
// Attachment previews live in a different key space: AttachmentPreviewService
// writes only '1x1' (images/docs) and 'video' (poster frames).
const FILE_VARIANT_KEYS = ['1x1', 'video', 'small', 'medium']

const EMPTY_PREVIEW: QuotePreview = { actionTags: [], media: [], overflowCount: 0, text: '' }

const KIND_LABELS: Record<QuoteMediaKind, string> = {
    image: 'Photo',
    video: 'Video',
    audio: 'Audio',
    file: 'File',
    link: 'Link',
    ordinal: 'Ordinal',
    poll: 'Poll',
}

// Relative /api/... media URLs need the API origin in split-origin dev — the
// same normalization ThinParent and AnswerImage apply. Guarded so the pure
// module still runs under node in unit tests.
function normalizeMediaUrl(rawUrl?: unknown): string | null {
    if (typeof rawUrl !== 'string' || !rawUrl.trim()) return null
    const url = rawUrl.trim()
    if (
        url.startsWith('//') ||
        url.startsWith('http://') ||
        url.startsWith('https://') ||
        url.startsWith('blob:') ||
        url.startsWith('data:')
    ) {
        return url
    }
    const apiBase = typeof window === 'undefined' ? null : (window as any)?.knovApiUrl
    if (url.startsWith('/api/') && apiBase) {
        return `${String(apiBase).replace(/\/+$/, '')}${url}`
    }
    return url
}

function preferredThumbUrl(
    variants: unknown,
    keys: string[],
    ...fallbacks: unknown[]
): string | null {
    if (variants && typeof variants === 'object' && !Array.isArray(variants)) {
        const map = variants as Record<string, unknown>
        for (const key of keys) {
            const found = normalizeMediaUrl(map[key])
            if (found) return found
        }
    }
    for (const fallback of fallbacks) {
        const found = normalizeMediaUrl(fallback)
        if (found) return found
    }
    return null
}

function asArray(value: unknown): any[] {
    return Array.isArray(value) ? value : []
}

function trimmedString(value: unknown): string {
    return typeof value === 'string' ? value.trim() : ''
}

// payloadMediaKind also answers 'run'; the quote line has no such chip.
function toQuoteKind(kind: string): QuoteMediaKind {
    return kind === 'image' || kind === 'video' || kind === 'audio' ? kind : 'file'
}

// A recording is audio or video by its declared type; the player treats a
// recording with a poster or an mp4 as video, and audio has no artwork.
function recordingMediaKind(answer: Record<string, any>): 'video' | 'audio' | null {
    if (!answer.recording && !answer.recording_url && !answer.mp4_recording_url) return null

    const declared = trimmedString(
        answer.recording_type || answer.original_recording_type,
    ).toLowerCase()
    if (declared.startsWith('audio')) return 'audio'
    if (declared.startsWith('video')) return 'video'
    if (answer.mp4_recording_url || answer.recording_thumbnail_url) return 'video'
    return payloadMediaKind({ url: trimmedString(answer.recording_url) }) === 'audio'
        ? 'audio'
        : 'video'
}

interface CollectedQuoteMedia {
    chip: QuoteMediaChip
    created_at: unknown
    attached_at: unknown
    blob_created_at: unknown
    supplemental: boolean
}

function mediaIdentityKeys(item: Record<string, any> | null | undefined): string[] {
    const attachmentId = item?.attachment_id || item?.attachmentId || item?.id
    const blobId = item?.blob_id || item?.blobId
    return [
        attachmentId ? `attachment:${String(attachmentId)}` : null,
        blobId ? `blob:${String(blobId)}` : null,
    ].filter((key): key is string => !!key)
}

function collectMedia(answer: Record<string, any>): QuoteMediaChip[] {
    const collected: CollectedQuoteMedia[] = []
    const seenUrls = new Set<string>()
    const seenIdentities = new Set<string>()

    // Media an AI action consumed is provenance, not content — the post body
    // demotes it to a compact input strip, so the quote line must not spend a
    // chip on it (and must not let references crowd out the generated output).
    const aiInputDescriptors = aiInputDescriptorsForAnswer(answer)
    const isAiInput = (item: unknown) =>
        !!aiInputDescriptors?.length &&
        !!aiInputDescriptorForMediaItem(item, aiInputDescriptors, { excludeSameRun: false })

    // AI outputs are frequently the post's own attachments — one media, one chip.
    const push = (
        chip: QuoteMediaChip,
        {
            identityKeys = [],
            createdAt = [],
            attachedAt = [],
            blobCreatedAt = [],
            supplemental = false,
        }: {
            identityKeys?: string[]
            createdAt?: unknown[]
            attachedAt?: unknown[]
            blobCreatedAt?: unknown[]
            supplemental?: boolean
        } = {},
    ) => {
        if (identityKeys.some(key => seenIdentities.has(key))) return
        if (chip.thumbUrl) {
            if (seenUrls.has(chip.thumbUrl)) return
            seenUrls.add(chip.thumbUrl)
        }
        identityKeys.forEach(key => seenIdentities.add(key))
        collected.push({
            chip,
            created_at: firstValidMediaTimestamp(...createdAt),
            attached_at: firstValidMediaTimestamp(...attachedAt),
            blob_created_at: firstValidMediaTimestamp(...blobCreatedAt),
            supplemental,
        })
    }

    const imagesV2 = sortMediaByCreation(asArray(answer.images_v2))
    const legacyImages = sortMediaByCreation(asArray(answer.image_urls))

    for (const image of imagesV2) {
        if (
            isAiInput({
                attachmentId: image?.attachment_id,
                blobId: image?.blob_id,
                urls: image?.urls,
                previewUrl: image?.preview_url,
            })
        ) {
            continue
        }
        push(
            {
                kind: 'image',
                thumbUrl: preferredThumbUrl(image?.urls, IMAGE_VARIANT_KEYS, image?.preview_url),
                label: trimmedString(image?.image_filename) || KIND_LABELS.image,
            },
            {
                identityKeys: mediaIdentityKeys(image),
                createdAt: [image?.created_at, image?.createdAt],
                attachedAt: [image?.attached_at, image?.attachedAt],
                blobCreatedAt: [image?.blob_created_at, image?.blobCreatedAt],
            },
        )
    }

    for (const image of legacyImages) {
        if (
            isAiInput({
                attachmentId: image?.attachment_id,
                urls: image?.preview_urls,
                previewUrl: image?.preview_url,
                url: image?.image_url,
            })
        ) {
            continue
        }
        push(
            {
                kind: 'image',
                thumbUrl: preferredThumbUrl(
                    image?.preview_urls,
                    IMAGE_VARIANT_KEYS,
                    image?.preview_url,
                ),
                label: trimmedString(image?.image_filename) || KIND_LABELS.image,
            },
            {
                identityKeys: mediaIdentityKeys(image),
                createdAt: [image?.created_at, image?.createdAt],
                attachedAt: [image?.attached_at, image?.attachedAt],
                blobCreatedAt: [image?.blob_created_at, image?.blobCreatedAt],
            },
        )
    }

    if (
        !imagesV2.length &&
        !legacyImages.length &&
        answer.answer_image_url &&
        !isAiInput(answerImageInputClassificationItem(answer))
    ) {
        push(
            {
                kind: 'image',
                thumbUrl: normalizeMediaUrl(answer.answer_image_url),
                label: KIND_LABELS.image,
            },
            {
                identityKeys: mediaIdentityKeys({
                    attachment_id: answer.image_attachment_id,
                    blob_id: answer.image_blob_id,
                }),
                createdAt: [answer.updated_at, answer.created_at],
            },
        )
    }

    const recordingKind = isAiInput(answerRecordingInputClassificationItem(answer))
        ? null
        : recordingMediaKind(answer)
    if (recordingKind === 'video') {
        push(
            {
                kind: 'video',
                thumbUrl: normalizeMediaUrl(answer.recording_thumbnail_url),
                label: KIND_LABELS.video,
            },
            {
                identityKeys: mediaIdentityKeys({
                    attachment_id:
                        answer.mp4_recording_attachment_id || answer.recording_attachment_id,
                    blob_id: answer.mp4_recording_blob_id || answer.recording_blob_id,
                }),
                createdAt: [answer.recording_created_at, answer.updated_at],
            },
        )
    } else if (recordingKind === 'audio') {
        push(
            { kind: 'audio', thumbUrl: null, label: KIND_LABELS.audio },
            {
                identityKeys: mediaIdentityKeys({
                    attachment_id:
                        answer.mp4_recording_attachment_id || answer.recording_attachment_id,
                    blob_id: answer.mp4_recording_blob_id || answer.recording_blob_id,
                }),
                createdAt: [answer.recording_created_at, answer.updated_at],
            },
        )
    }

    for (const file of asArray(answer.file_urls)) {
        if (isAiInput(fileInputClassificationItem(file))) continue
        const kind = toQuoteKind(
            payloadMediaKind({
                url: file?.url,
                name: file?.name,
                content_type: file?.content_type,
            }),
        )
        push(
            {
                kind,
                thumbUrl:
                    kind === 'audio'
                        ? null
                        : // An image dropped in as a file attachment carries the image
                          // key space in `urls` and leaves preview_urls empty, so look
                          // for the 120px variant before settling for the 600px
                          // preview_url in a 16px chip.
                          preferredThumbUrl(file?.urls, IMAGE_VARIANT_KEYS) ||
                          preferredThumbUrl(
                              file?.preview_urls,
                              FILE_VARIANT_KEYS,
                              file?.preview_url,
                          ),
                label: trimmedString(file?.name) || KIND_LABELS[kind],
            },
            {
                identityKeys: mediaIdentityKeys(file),
                createdAt: [file?.created_at, file?.createdAt, answer.updated_at],
            },
        )
    }

    for (const run of asArray(answer.ai_runs)) {
        if (run?.status && run.status !== 'succeeded') continue
        for (const output of asArray(run?.output_urls)) {
            if (aiRunOutputIsConsumedInput(run, output, aiInputDescriptors)) continue
            const kind = toQuoteKind(payloadMediaKind(output))
            push(
                {
                    kind,
                    thumbUrl:
                        kind === 'audio'
                            ? null
                            : preferredThumbUrl(
                                  output?.urls,
                                  IMAGE_VARIANT_KEYS,
                                  output?.preview_url,
                              ),
                    label: KIND_LABELS[kind],
                },
                {
                    identityKeys: mediaIdentityKeys(output),
                    createdAt: [
                        output?.created_at,
                        output?.createdAt,
                        run?.created_at,
                        run?.createdAt,
                    ],
                },
            )
        }
    }

    const url = answer.url
    const isOrdinal = !!url?.bsv_ordinal_id || !!answer.has_ordinal
    if (url && (url.address || url.title)) {
        push(
            {
                kind: isOrdinal ? 'ordinal' : 'link',
                thumbUrl: preferredThumbUrl(
                    url.preview_urls,
                    IMAGE_VARIANT_KEYS,
                    url.preview_image_url,
                    url.preview_url,
                    asArray(url.image_urls)[0],
                ),
                label: isOrdinal ? KIND_LABELS.ordinal : KIND_LABELS.link,
            },
            {
                supplemental: true,
            },
        )
    } else if (isOrdinal && !collected.length) {
        // `has_ordinal` describes the answer's OWN image or recording being
        // inscribed — it is a property of media already chipped, not a second
        // medium. Only a post with nothing else to show leads with it.
        push(
            { kind: 'ordinal', thumbUrl: null, label: KIND_LABELS.ordinal },
            { supplemental: true },
        )
    }

    if (asArray(answer.poll_options_payload).length) {
        push({ kind: 'poll', thumbUrl: null, label: KIND_LABELS.poll }, { supplemental: true })
    }

    return [
        ...sortMediaByCreation(collected.filter(item => !item.supplemental)),
        ...collected.filter(item => item.supplemental),
    ].map(({ chip }) => chip)
}

export function quotePreviewForAnswer(answer: Partial<Answer> | null | undefined): QuotePreview {
    // No id means the target is still resolving — QuoteLine shows its loader.
    if (!answer?.id) return EMPTY_PREVIEW

    const record = answer as Record<string, any>
    // Token labels and body text come from the shared display projection, so
    // the quote line resolves displayName/prompt privacy exactly as the post
    // body does. Prompts stay out of the chips; a policy-public prompt is
    // ordinary text and lands in `text`.
    const parts = getAnswerDisplayParts(record)
    const allTags = parts.tokens.map(token => token.label).filter(Boolean)
    const actionTags = allTags.slice(0, ACTION_TAG_LIMIT)

    const allMedia = collectMedia(record)
    const media = allMedia.slice(0, MEDIA_CHIP_LIMIT)
    const overflowCount = allMedia.length - media.length + (allTags.length - actionTags.length)

    const bodyText = trimmedString(parts.text)
    const linkText = trimmedString(record.url?.title) || trimmedString(record.url?.address)
    // A thumbnail is self-evident; a bare glyph is not, so an icon-only leading
    // chip lends the line its label. With neither text nor media, a post is
    // still a post.
    const leadingIconLabel = media.length && !media[0].thumbUrl ? media[0].label : ''
    const text =
        bodyText || linkText || leadingIconLabel || (media.length || allTags.length ? '' : 'Post')

    return { actionTags, media, overflowCount, text }
}

// One-string projection for surfaces that cannot render chips (the composer
// chip on claude/bottom-editor).
export function quoteSnippetFromPreview(preview: QuotePreview): string {
    if (preview.text) return preview.text
    if (preview.actionTags.length) return preview.actionTags[0]
    if (preview.media.length) return preview.media[0].label
    return ''
}
